import { getGpuInfo, isIOS, isIPhone } from '../../../utilis/device-detection';
import FinalYogaTickvoice from '../../../assets/voices/Timer - final tick.mp3';
import getReadyMusic from '../../../assets/voices/Get-ready-music.mp3';
import replay from '../../../assets/svgs/replay.svg';
import ExitIcon from '../../../assets/svgs/exit-movex.svg';
import connectionLot from '../../../assets/svgs/connection-lost.svg';
import REMOTE_TICK_URL from '../../../assets/voices/tick.mp3';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import useChangeCounterValue from '../../../features/exercise/hooks/use-change-counter-value';
import useTransCriptText from '../../../features/exercise/hooks/use-transcript-text';
import { FEATURES } from '@config/features';

import 'twin.macro';

/**
 * Extract the device ID whether audioDevice is passed as:
 * - a string
 * - a MediaDeviceInfo object
 */
const getAudioDeviceId = audioDevice => {
  if (typeof audioDevice === 'string') {
    return audioDevice;
  }

  return audioDevice?.deviceId || null;
};

/**
 * Safari versions that do not support setSinkId should still play
 * through the default iPhone output.
 */
const setAudioOutputDevice = async (audio, audioDevice) => {
  const deviceId = getAudioDeviceId(audioDevice);

  if (!audio || !deviceId || typeof audio.setSinkId !== 'function') {
    return;
  }

  // setSinkId is unsupported on iOS (the guard above already skips it there);
  // on desktop, skip re-applying the same device on every single tick.
  if (audio.dataset.sinkId === deviceId) {
    return;
  }

  try {
    await audio.setSinkId(deviceId);
    audio.dataset.sinkId = deviceId;
  } catch (error) {
    console.warn('Failed to select audio output device.', {
      name: error?.name,
      message: error?.message,
      deviceId,
    });
  }
};

/**
 * Play one audio element safely.
 *
 * Every sound has its own HTMLAudioElement, so different sounds
 * can continue playing simultaneously.
 */
const playAudioElement = async ({ audio, name, audioDevice, restart = true, shouldPlay }) => {
  if (!audio || shouldPlay?.() === false) {
    return false;
  }

  const source = audio.currentSrc || audio.src;

  if (!source) {
    console.warn(`Cannot play ${name}: audio source is missing.`);
    return false;
  }

  try {
    await setAudioOutputDevice(audio, audioDevice);

    if (shouldPlay?.() === false) {
      return false;
    }

    if (restart) {
      audio.currentTime = 0;
    }

    await audio.play();

    return true;
  } catch (error) {
    console.error(`Failed to play ${name}.`, {
      name: error?.name,
      message: error?.message,
      source,
      readyState: audio.readyState,
      networkState: audio.networkState,
      mediaErrorCode: audio.error?.code,
      mediaErrorMessage: audio.error?.message,
      paused: audio.paused,
      muted: audio.muted,
      currentTime: audio.currentTime,
    });

    return false;
  }
};

const AudioSection = ({
  audioDevice,
  currentCounter,
  getReadyCountdownValue,
  voiceResult,
  soundRef,
  dispatch,
  yogaMusicRef,
  finalTickRef,
  audioSectionRef,
  isRealtimeActive,
  isSessionEnding = false,
  // Whether the "get ready" moment is still live: in-performance with the pause
  // modal closed. Anything that closes that window — the modal opening, or the
  // stage leaving IN_PERFORMANCE (a restart drops back to INTRO) — must cut the
  // clip short. Not the same as "the countdown is still counting": the clip runs
  // a little past 0 on purpose, so it is deliberately not tied to
  // getReadyCountdownValue going null.
  isGetReadyWindowOpen = false,
}) => {
  const [voiceArray, setVoiceArray] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gpuInfo, setGpuInfo] = useState(null);

  const audioUnlockedRef = useRef(false);
  const tickAudioCtxRef = useRef(null);
  const silentKeepAliveRef = useRef(null);
  const audioBuffersRef = useRef({});
  const audioBufferLoadsRef = useRef({});
  const voiceRequestIdRef = useRef(0);
  const previousGetReadyCountdownRef = useRef(null);
  const audioEnabledRef = useRef(!isSessionEnding);
  audioEnabledRef.current = !isSessionEnding;

  const { lastCounter } = useChangeCounterValue(currentCounter);

  const { setCurrentVoiceIndex } = useTransCriptText({
    dispatch,
    voiceArray: voiceArray?.audio_content,
  });

  const finalTickSource = FinalYogaTickvoice;

  const getReadySource = getReadyMusic;

  /**
   * Short one-shot sounds (counter tick, get-ready countdown) play via Web
   * Audio instead of an <audio> element. Repeatedly resetting currentTime and
   * calling play() on the same <audio> element for a rapid-fire or
   * unlock-gated sound is what makes iOS Safari intermittently drop the
   * source. Decoding each mp3 once into an AudioBuffer and firing a new
   * BufferSource per play avoids reload/seek entirely and lets overlapping
   * plays coexist.
   */
  const ensureTickAudioContext = useCallback(() => {
    if (tickAudioCtxRef.current) {
      return tickAudioCtxRef.current;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      return null;
    }

    tickAudioCtxRef.current = new AudioContextClass();

    return tickAudioCtxRef.current;
  }, []);

  /**
   * Resuming the context isn't enough on iOS: a hardware audio route that
   * has gone quiet between one-shot sounds is exactly what iOS gates behind
   * a fresh gesture again, so a get-ready/tick BufferSource fired from a
   * WS-driven effect (no gesture behind it) can still land silently even
   * with ctx.state === 'running'. The standard fix is to keep the route
   * continuously active with an inaudible (gain 0) looping buffer, started
   * once from a real gesture — with output never actually going quiet, iOS
   * has nothing to gate later one-shot sounds behind.
   */
  const startSilentKeepAlive = useCallback(ctx => {
    if (!ctx || silentKeepAliveRef.current) {
      return;
    }

    try {
      const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      const gain = ctx.createGain();
      gain.gain.value = 0;

      source.connect(gain);
      gain.connect(ctx.destination);
      source.start(0);

      silentKeepAliveRef.current = source;
    } catch (error) {
      console.warn('Failed to start the silent keep-alive buffer.', {
        name: error?.name,
        message: error?.message,
      });
    }
  }, []);

  const loadAudioBuffer = useCallback((ctx, url) => {
    if (audioBuffersRef.current[url]) {
      return Promise.resolve(audioBuffersRef.current[url]);
    }

    if (!audioBufferLoadsRef.current[url]) {
      audioBufferLoadsRef.current[url] = fetch(url)
        .then(response => response.arrayBuffer())
        .then(data => ctx.decodeAudioData(data))
        .then(buffer => {
          audioBuffersRef.current[url] = buffer;
          return buffer;
        })
        .catch(error => {
          delete audioBufferLoadsRef.current[url];
          console.error('Failed to load/decode audio buffer.', {
            url,
            name: error?.name,
            message: error?.message,
          });
          throw error;
        });
    }

    return audioBufferLoadsRef.current[url];
  }, []);

  const playBufferSound = useCallback(
    (url, { label, debugSource, onSourceCreated }) => {
      if (!audioEnabledRef.current) {
        return;
      }

      const ctx = ensureTickAudioContext();

      if (!ctx) {
        console.warn(`Web Audio unsupported: ${label} skipped.`);
        return;
      }

      void (async () => {
        try {
          if (ctx.state === 'suspended') {
            await ctx.resume();
          }

          const buffer = await loadAudioBuffer(ctx, url);
          const bufferSource = ctx.createBufferSource();

          bufferSource.buffer = buffer;
          bufferSource.connect(ctx.destination);
          // Both awaits above can span a pause/stage change, and a caller that
          // tracks its own node has no node to stop until this line. Returning
          // false from onSourceCreated is how it says "that window closed while
          // you were decoding" — the source is discarded unstarted rather than
          // started and then stopped, which is what made a cancelled get-ready
          // clip still play. Callers that return nothing are unaffected.
          if (onSourceCreated?.(bufferSource) === false) {
            bufferSource.disconnect();
            return;
          }
          bufferSource.start(0);
        } catch (error) {
          console.error(`Failed to play ${label}.`, {
            source: debugSource,
            name: error?.name,
            message: error?.message,
            contextState: ctx.state,
          });
        }
      })();
    },
    [ensureTickAudioContext, loadAudioBuffer]
  );

  const playCounterTick = useCallback(
    (debugSource = 'counter') =>
      playBufferSound(REMOTE_TICK_URL, { label: 'counter tick', debugSource }),
    [playBufferSound]
  );

  // The get-ready clip runs a few seconds — long enough that opening the pause
  // menu mid-playback is common. Keep a handle on its buffer source so that
  // can be cut short (see the isGetReadyWindowOpen effect below); nothing else needs
  // one, they're all short one-shot sounds.
  const getReadySourceNodeRef = useRef(null);
  // Bumped every time the get-ready window closes. playBufferSound awaits a
  // context resume and a buffer decode before it hands the node back, so a
  // window that closes inside that gap finds no node to stop; comparing the
  // generation captured at call time against the current one is how that play
  // gets cancelled instead of starting late.
  const getReadyGenerationRef = useRef(0);
  const playGetReadySound = useCallback(() => {
    const generation = getReadyGenerationRef.current;

    return playBufferSound(getReadySource, {
      label: 'get-ready countdown sound',
      onSourceCreated: node => {
        if (getReadyGenerationRef.current !== generation) {
          return false;
        }

        getReadySourceNodeRef.current = node;
        node.onended = () => {
          if (getReadySourceNodeRef.current === node) {
            getReadySourceNodeRef.current = null;
          }
        };
        return true;
      },
    });
  }, [playBufferSound, getReadySource]);

  /**
   * iOS suspends the shared tick AudioContext on its own (backgrounding, a
   * brief interruption, sometimes for no visible reason) well after the
   * initial unlock below. A `ctx.resume()` called later from a WS-driven
   * effect (get-ready countdown, counter tick) is NOT a user gesture as far
   * as iOS is concerned, and can sit there without ever actually flipping the
   * context back to "running" — so the sound scheduled against it stalls
   * until the next real tap resumes it for real, at which point it and
   * whatever else was queued fire together. Unlike unlockAudioElements below,
   * this is NOT one-time — it runs on every real interaction for as long as
   * the page is open, so the context is resumed from a genuine gesture as
   * often as iOS needs it to be.
   */
  const resumeTickAudioContext = useCallback(async () => {
    const ctx = ensureTickAudioContext();

    if (!ctx) {
      return;
    }

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (error) {
        console.warn('Failed to resume tick AudioContext from a user gesture.', {
          name: error?.name,
          message: error?.message,
        });
      }
    }

    if (ctx.state === 'running') {
      startSilentKeepAlive(ctx);
    }
  }, [ensureTickAudioContext, startSilentKeepAlive]);

  /**
   * Safari on iPhone requires audio playback to start from a real
   * user interaction.
   *
   * We briefly start and stop every remaining audio element on the
   * first pointer, touch or keyboard interaction, and create/resume
   * the tick AudioContext in the same gesture (iOS only allows that
   * from a direct user-interaction call stack). This part only needs to run
   * once — resumeTickAudioContext above is what keeps the context usable
   * after that.
   */
  const unlockAudioElements = useCallback(async () => {
    if (audioUnlockedRef.current) {
      return;
    }

    const tickCtx = ensureTickAudioContext();
    let tickUnlocked = false;

    if (tickCtx) {
      try {
        if (tickCtx.state === 'suspended') {
          await tickCtx.resume();
        }

        tickUnlocked = tickCtx.state === 'running';

        if (tickUnlocked) {
          startSilentKeepAlive(tickCtx);
        }
      } catch (error) {
        console.warn('Failed to resume tick AudioContext.', {
          name: error?.name,
          message: error?.message,
        });
      }

      void loadAudioBuffer(tickCtx, REMOTE_TICK_URL).catch(() => {});
      void loadAudioBuffer(tickCtx, getReadySource).catch(() => {});
    }

    const audioElements = [soundRef?.current, finalTickRef?.current, yogaMusicRef?.current].filter(
      Boolean
    );

    if (!audioElements.length) {
      if (tickUnlocked) {
        audioUnlockedRef.current = true;
      }

      return;
    }

    const results = await Promise.allSettled(
      audioElements.map(async audio => {
        const wasMuted = audio.muted;
        const previousTime = audio.currentTime;

        /*
         * Muting prevents the initialization sounds from being heard.
         * The play call is still executed from the user's gesture.
         */
        audio.muted = true;

        try {
          await audio.play();

          audio.pause();

          try {
            audio.currentTime = previousTime || 0;
          } catch {
            audio.currentTime = 0;
          }

          return true;
        } finally {
          audio.muted = wasMuted;
        }
      })
    );

    const hasSuccessfulUnlock =
      tickUnlocked || results.some(result => result.status === 'fulfilled');

    if (hasSuccessfulUnlock) {
      audioUnlockedRef.current = true;
    }
  }, [
    ensureTickAudioContext,
    finalTickRef,
    getReadySource,
    loadAudioBuffer,
    soundRef,
    startSilentKeepAlive,
    yogaMusicRef,
  ]);

  /**
   * Listen to every real user interaction anywhere on the page — not just
   * the first one. unlockAudioElements is still one-shot (the <audio>
   * element dance only needs doing once), but resumeTickAudioContext must
   * run on every gesture: iOS can re-suspend the shared tick AudioContext at
   * any point after that first unlock.
   *
   * This avoids requiring a special "Enable sound" button, but the
   * exercise page should mount AudioSection before the user presses Start.
   */
  useEffect(() => {
    const handleUserInteraction = () => {
      void resumeTickAudioContext();
      void unlockAudioElements();
    };

    document.addEventListener('pointerdown', handleUserInteraction, true);

    document.addEventListener('touchend', handleUserInteraction, true);

    document.addEventListener('keydown', handleUserInteraction, true);

    return () => {
      document.removeEventListener('pointerdown', handleUserInteraction, true);

      document.removeEventListener('touchend', handleUserInteraction, true);

      document.removeEventListener('keydown', handleUserInteraction, true);
    };
  }, [resumeTickAudioContext, unlockAudioElements]);

  /**
   * Counter sound.
   */
  useEffect(() => {
    if (isSessionEnding || currentCounter <= lastCounter) {
      return;
    }

    playCounterTick('counter-effect');
  }, [currentCounter, isSessionEnding, lastCounter, playCounterTick]);

  /**
   * Get-ready ("3, 2, 1") countdown sound — same Web Audio mechanism as the
   * counter tick above, fired once per countdown (null -> a number), not on
   * every tick of it.
   */
  useEffect(() => {
    const previous = previousGetReadyCountdownRef.current;
    previousGetReadyCountdownRef.current = getReadyCountdownValue;

    if (isSessionEnding || previous != null || getReadyCountdownValue == null) {
      return;
    }

    playGetReadySound();
  }, [getReadyCountdownValue, isSessionEnding, playGetReadySound]);

  // Cuts the get-ready clip short the moment its window closes — the pause menu
  // opening mid-playback (it would keep talking over a modal that just told the
  // backend to pause), or the stage leaving IN_PERFORMANCE. The second case is
  // what a Restart does: it rewinds to INTRO, and the clip for a countdown that
  // is no longer happening must not keep playing.
  //
  // onended (set in playGetReadySound) clears the ref on its own once the clip
  // finishes naturally, so this only ever stops a still-live source. The
  // generation bump additionally cancels a play that is still mid-await inside
  // playBufferSound and has therefore not registered a node to stop yet.
  useEffect(() => {
    if (isGetReadyWindowOpen) {
      return;
    }

    getReadyGenerationRef.current += 1;

    const node = getReadySourceNodeRef.current;
    if (!node) {
      return;
    }

    getReadySourceNodeRef.current = null;
    try {
      node.stop();
    } catch (error) {
      // Already stopped/ended — nothing to do.
    }
  }, [isGetReadyWindowOpen]);

  /**
   * Tick heartbeat: plays the counter tick every second.
   */
  // useEffect(() => {
  //   const timer = window.setInterval(() => {
  //     void playAudioElement({
  //       audio: tickAudioRef.current,
  //       name: 'tick heartbeat',
  //       audioDevice,
  //     });
  //   }, 1000);

  //   return () => window.clearInterval(timer);
  // }, [audioDevice]);

  useEffect(() => {
    if (!isSessionEnding) {
      return;
    }

    voiceRequestIdRef.current += 1;
    [soundRef?.current, finalTickRef?.current, yogaMusicRef?.current]
      .filter(Boolean)
      .forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    setIsPlaying(false);
  }, [finalTickRef, isSessionEnding, soundRef, yogaMusicRef]);

  /**
   * Read GPU vendor/renderer once for the on-screen debug readout.
   */
  useEffect(() => {
    setGpuInfo(getGpuInfo());
  }, []);

  /**
   * Store the latest voice response.
   */
  useEffect(() => {
    if (!voiceResult?.audio_url) {
      return;
    }

    setVoiceArray(voiceResult);
  }, [voiceResult]);

  /**
   * Stop the backend voice clip while the realtime voice agent
   * is controlling speech.
   *
   * Other audio elements are not stopped.
   */
  useEffect(() => {
    if (!isRealtimeActive) {
      return;
    }

    const audio = soundRef?.current;

    if (!audio) {
      return;
    }

    audio.pause();
    audio.currentTime = 0;

    setIsPlaying(false);
  }, [isRealtimeActive, soundRef]);

  /**
   * Play the backend voice clip using the existing soundRef element.
   *
   * Do not create a new Audio() instance here. The existing element
   * was unlocked during the user's initial interaction.
   */
  useEffect(() => {
    const voiceUrl = voiceArray?.audio_url;
    const audio = soundRef?.current;

    if (!voiceUrl || !audio || isRealtimeActive) {
      return;
    }

    let cancelled = false;
    const requestId = ++voiceRequestIdRef.current;

    const playVoice = async () => {
      /*
       * Stop only the previous voice instruction.
       * Counter, holding and timer sounds remain independent.
       */
      audio.pause();
      audio.currentTime = 0;

      /*
       * Set the source directly before load() so Safari prepares
       * this exact URL on the already-unlocked audio element.
       */
      if (audio.src !== voiceUrl) {
        audio.src = voiceUrl;
      }

      audio.load();

      try {
        /*
         * loadedmetadata may already have fired, so do not wait for
         * it unconditionally.
         */
        if (audio.readyState === HTMLMediaElement.HAVE_NOTHING) {
          await new Promise((resolve, reject) => {
            const handleReady = () => {
              cleanup();
              resolve();
            };

            const handleError = () => {
              cleanup();
              reject(audio.error || new Error('Voice audio could not be loaded.'));
            };

            const cleanup = () => {
              audio.removeEventListener('loadedmetadata', handleReady);

              audio.removeEventListener('canplay', handleReady);

              audio.removeEventListener('error', handleError);
            };

            audio.addEventListener('loadedmetadata', handleReady, { once: true });

            audio.addEventListener('canplay', handleReady, { once: true });

            audio.addEventListener('error', handleError, { once: true });
          });
        }

        if (cancelled || requestId !== voiceRequestIdRef.current) {
          return;
        }

        if (!audioEnabledRef.current) {
          return;
        }

        const played = await playAudioElement({
          audio,
          name: 'voice instruction',
          audioDevice,
          shouldPlay: () => audioEnabledRef.current,
        });

        if (cancelled || requestId !== voiceRequestIdRef.current) {
          return;
        }

        setIsPlaying(played);

        if (played) {
          setCurrentVoiceIndex?.(0);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        setIsPlaying(false);

        console.error('Failed to prepare voice audio.', {
          name: error?.name,
          message: error?.message,
          voiceUrl,
        });
      }
    };

    void playVoice();

    return () => {
      cancelled = true;
    };
  }, [audioDevice, isRealtimeActive, setCurrentVoiceIndex, soundRef, voiceArray?.audio_url]);

  /**
   * Keep React state synchronized with the voice element.
   */
  useEffect(() => {
    const audio = soundRef?.current;

    if (!audio) {
      return;
    }

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentVoiceIndex?.(0);
    };

    const handleError = () => {
      setIsPlaying(false);

      console.error('Voice audio element error.', {
        mediaErrorCode: audio.error?.code,
        source: audio.currentSrc,
      });
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [setCurrentVoiceIndex, soundRef]);

  /**
   * Pause all sounds when the browser tab is hidden; resume the tick
   * AudioContext on return since iOS suspends it in the background.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const audioElements = [
          soundRef?.current,
          finalTickRef?.current,
          yogaMusicRef?.current,
        ].filter(Boolean);

        audioElements.forEach(audio => {
          if (!audio.paused) {
            audio.pause();
          }
        });

        setIsPlaying(false);
        return;
      }

      if (tickAudioCtxRef.current?.state === 'suspended') {
        tickAudioCtxRef.current.resume().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [finalTickRef, soundRef, yogaMusicRef]);

  /**
   * Release the AudioContext on unmount.
   */
  useEffect(() => {
    return () => {
      tickAudioCtxRef.current?.close().catch(() => {});
    };
  }, []);

  const section = (
    <div ref={audioSectionRef} tw="z-[100]" data-voice-playing={isPlaying}>
      <img loading="lazy" decoding="async" src={ExitIcon} alt="" tw="hidden opacity-0 -z-10" />

      <img loading="lazy" decoding="async" src={connectionLot} alt="" tw="hidden opacity-0 -z-10" />

      <img loading="lazy" decoding="async" src={replay} alt="" tw="hidden opacity-0 -z-10" />

      {/* Voice instruction
          A valid fallback source is important because this element must
          exist and be unlockable before audio_url arrives from the server.
          Never heard: the unlock mutes the element, and the voice effect
          assigns the real audio_url before it ever plays — the file here only
          has to be something the element can load. */}
      <audio
        ref={soundRef}
        src={voiceArray?.audio_url || finalTickSource}
        preload="auto"
        playsInline
      />

      {/* Counter tick plays via Web Audio now — no <audio> element needed */}

      {/* Final timer */}
      <audio ref={finalTickRef} src={finalTickSource} preload="auto" playsInline />

      {/* Dev-only: compare a direct-gesture tick against the WS-triggered one */}
      {FEATURES.AUDIO_DEBUG_CONTROLS && (
        <button
          type="button"
          onClick={() => playCounterTick('direct-click')}
          tw="fixed bottom-2 right-2 z-[999] rounded bg-black/60 px-2 py-1 text-[10px] text-white"
        >
          Test tick
        </button>
      )}
    </div>
  );

  return (
    <>
      {section}

      {/* Device detection readout. Portal to body so it stays above every
          other layer instead of AudioSection's own z-index stack. Dev-only. */}
      {FEATURES.AUDIO_DEBUG_CONTROLS &&
        createPortal(
          <div tw="fixed bottom-2 left-1/2 -translate-x-1/2 z-[10000] max-w-[220px] rounded bg-black/60 px-2 py-1 text-[10px] text-white">
            <div>
              iPhone: {String(isIPhone())} | iOS: {String(isIOS())}
            </div>
            <div>GPU: {gpuInfo?.renderer || 'unavailable'}</div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AudioSection;
